import { useEffect, useCallback } from "react";
import { useUISlice, ColorScheme } from "./useUISlice";
import { useAppStore } from "@/controller";
import { getPlatform, getPlatformRuntime, isNativeApp } from "@/platform";
import { actions } from "@/controller";
import { attachSystemThemeListener } from "@/theme-events";

type ResolvedTheme = "light" | "dark";

const DARK_THEME_COLOR = "#111022";
const LIGHT_THEME_COLOR = "#eff3fd";

/**
 * Write the browser-chrome theme-color.
 *
 * Safari on iOS often fails to repaint the address bar / status area when an
 * existing <meta name="theme-color"> only has its `content` mutated via
 * setAttribute — the DOM updates but the chrome keeps the old tint. Replacing
 * the node (remove + re-insert a fresh element) forces Safari to re-read it.
 */
const writeThemeColorMeta = (color: string): void => {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll('meta[name="theme-color"]')
    .forEach((node) => node.remove());
  const meta = document.createElement("meta");
  meta.setAttribute("name", "theme-color");
  meta.setAttribute("content", color);
  document.head.appendChild(meta);
};

/**
 * Get the system's preferred color scheme
 */
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === "undefined") return "dark";
  try {
    const mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
    return mediaQuery?.matches ? "dark" : "light";
  } catch {
    return "dark";
  }
};

/**
 * Resolve the effective theme based on preference and system setting
 */
const resolveTheme = (preference: ColorScheme): ResolvedTheme => {
  if (preference === "system") {
    return getSystemTheme();
  }
  return preference;
};

/**
 * Update status bar style based on theme (iOS and Android)
 * Style.Dark = dark content (for light backgrounds)
 * Style.Light = light content (for dark backgrounds)
 *
 * On iOS, the status bar background is transparent and takes the app's background.
 * On Android, we also set the background color explicitly.
 */
export const updateStatusBarStyle = (theme: ResolvedTheme): void => {
  const platform = getPlatform();
  if (platform !== "android" && platform !== "ios") return;

  // Set status bar text color based on theme
  getPlatformRuntime().statusBar.setStyle(theme === "dark" ? "dark" : "light").catch(() => {
    // Silently ignore errors (e.g., when running in browser)
  });

  // On Android, also set the background color
  if (platform === "android") {
    const bgColor = theme === "dark" ? DARK_THEME_COLOR : LIGHT_THEME_COLOR;
    getPlatformRuntime().statusBar.setBackgroundColor(bgColor).catch(() => {
      // Silently ignore errors
    });
  }
};

/**
 * Apply the theme to the document
 */
const applyTheme = (theme: ResolvedTheme): void => {
  if (typeof document === "undefined") return;

  // Set data-theme attribute for CSS variables
  document.documentElement.dataset.theme = theme;

  // Update meta theme-color for browser chrome
  writeThemeColorMeta(
    theme === "dark" ? DARK_THEME_COLOR : LIGHT_THEME_COLOR,
  );

  // Update native status bar (iOS and Android)
  updateStatusBarStyle(theme);
};

/**
 * Initialize theme on app load (call this early, before React hydration)
 */
export const initializeTheme = (): void => {
  // Get theme from user data or default to system
  const userTheme = useAppStore.getState().userData?.theme;
  const preference: ColorScheme = userTheme || "system";
  const resolved = resolveTheme(preference);
  applyTheme(resolved);

  // Update the Zustand store
  useUISlice.getState().setColorScheme(preference);
};

/**
 * Hook to manage theme state and reactivity
 */
export const useTheme = () => {
  const colorScheme = useUISlice((state) => state.colorScheme);
  const setColorScheme = useUISlice((state) => state.setColorScheme);
  const userTheme = useAppStore((state) => state.userData?.theme);

  const resolvedTheme = resolveTheme(colorScheme);

  // Change theme preference and persist to database
  const setTheme = useCallback(
    (newScheme: ColorScheme) => {
      // Update local UI state immediately
      setColorScheme(newScheme);
      applyTheme(resolveTheme(newScheme));

      // Persist to database
      void actions.user.update({ theme: newScheme });
    },
    [setColorScheme]
  );

  // Apply theme when colorScheme changes
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Listen for system preference changes
  useEffect(() => {
    if (colorScheme !== "system") return;
    if (typeof window === "undefined" || !window.matchMedia) return;

    try {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      if (!mediaQuery) return;

      const handleChange = () => {
        applyTheme(getSystemTheme());
      };

      return attachSystemThemeListener(mediaQuery, handleChange);
    } catch {
      // matchMedia not supported, skip listener
    }
  }, [colorScheme]);

  // Re-apply theme when app resumes (to catch system theme changes on Android)
  useEffect(() => {
    if (!isNativeApp()) return;

    const handleAppStateChange = async (state: { isActive: boolean }) => {
      if (state.isActive) {
        // Re-apply theme when app becomes active
        const resolved = resolveTheme(colorScheme);
        applyTheme(resolved);
      }
    };

    const listener = getPlatformRuntime().app.onStateChange(handleAppStateChange);

    return () => {
      listener.then((l) => l.remove());
    };
  }, [colorScheme]);

  // Sync from userData.theme on mount and when it changes
  useEffect(() => {
    const theme = userTheme || "system";
    if (theme !== colorScheme) {
      setColorScheme(theme);
      applyTheme(resolveTheme(theme));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTheme]);

  return {
    /** Current preference: 'light' | 'dark' | 'system' */
    colorScheme,
    /** Resolved theme: 'light' | 'dark' (system resolved) */
    resolvedTheme,
    /** Set theme preference */
    setTheme,
    /** Check if dark mode is active */
    isDark: resolvedTheme === "dark",
    /** Check if light mode is active */
    isLight: resolvedTheme === "light",
  };
};

/**
 * Update meta theme-color (for modals that need different colors)
 */
export const setMetaThemeColor = (color: string): void => {
  writeThemeColorMeta(color);
};

/**
 * Reset meta theme-color to match current theme
 */
export const resetMetaThemeColor = (): void => {
  const resolved = resolveTheme(useUISlice.getState().colorScheme);
  const themeColor =
    resolved === "dark" ? DARK_THEME_COLOR : LIGHT_THEME_COLOR;
  setMetaThemeColor(themeColor);
};
